import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Donation control rendered in the cart drawer and cart page.
 *
 * Quick-add buttons add a donation immediately; "Different amount" swaps the
 * buttons for a free-entry field with its own "Add" button. Pricing uses the
 * configured variant's unit price as a multiplier (quantity = entered dollars
 * for a $1 variant), and the charged amount is stored as a line item property.
 * After a successful add we dispatch a CartAddEvent so every cart-items-component
 * (drawer + page) re-renders with the new line and total.
 */
class CartDonation extends HTMLElement {
  #variantId = 0;
  #unitPrice = 0;
  #symbol = '$';
  #moneyFormat = '${{amount}}';
  #currency = 'USD';
  #isSubmitting = false;

  connectedCallback() {
    this.#variantId = parseInt(this.dataset.variantId || '0', 10);
    this.#unitPrice = parseInt(this.dataset.unitPrice || '0', 10);
    this.#symbol = this.dataset.currencySymbol || '$';
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    this.#currency = this.dataset.currency || this.#currency;

    // Listeners are delegated from the host element so they survive the section
    // morph that follows a successful add (which can replace inner nodes).
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('input', this.#handleInput);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('input', this.#handleInput);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  get #input() {
    return this.querySelector('[data-donation-input]');
  }

  get #error() {
    return this.querySelector('[data-donation-error]');
  }

  get #popover() {
    return this.querySelector('[data-donation-popover]');
  }

  get #trigger() {
    return this.querySelector('[data-donation-trigger]');
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-donation-trigger]')) {
      this.#togglePopover();
      return;
    }

    const preset = target.closest('[data-donation-amount]');
    if (preset instanceof HTMLElement) {
      this.#add(preset.dataset.donationAmount ?? '');
      return;
    }

    if (target.closest('[data-donation-custom-toggle]')) {
      this.#showCustomField();
      return;
    }

    if (target.closest('[data-donation-add]')) {
      this.#add(this.#input?.value ?? '');
    }
  };

  #handleInput = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-donation-input]')) {
      this.#clearError();
    }
  };

  #showCustomField() {
    this.querySelector('[data-donation-presets]')?.setAttribute('hidden', '');
    this.querySelector('[data-donation-custom]')?.removeAttribute('hidden');
    const input = this.#input;
    if (input instanceof HTMLInputElement) input.focus();
  }

  #togglePopover() {
    if (this.#popover?.hasAttribute('hidden')) this.#openPopover();
    else this.#closePopover();
  }

  #openPopover() {
    const popover = this.#popover;
    if (!popover) return;
    // Always reopen on the preset view, not a half-finished custom takeover.
    this.querySelector('[data-donation-presets]')?.removeAttribute('hidden');
    this.querySelector('[data-donation-custom]')?.setAttribute('hidden', '');
    this.#clearError();

    popover.removeAttribute('hidden');
    this.#trigger?.setAttribute('aria-expanded', 'true');
    // The opening click bubbles to document after this element, but #handleOutsideClick
    // ignores clicks contained within the component, so it won't self-close.
    document.addEventListener('click', this.#handleOutsideClick);
    document.addEventListener('keydown', this.#handleKeydown);
    popover.querySelector('button')?.focus();
  }

  #closePopover() {
    const popover = this.#popover;
    if (!popover || popover.hasAttribute('hidden')) return;
    popover.setAttribute('hidden', '');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  #handleOutsideClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.#closePopover();
    }
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closePopover();
      this.#trigger?.focus();
    }
  };

  async #add(rawAmount) {
    if (this.#isSubmitting) return;

    if (!this.#variantId) {
      this.#showError('Donation product is not configured.');
      return;
    }

    const amount = parseFloat(String(rawAmount).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amount) || amount < 1) {
      this.#showError(`Please enter an amount of ${this.#symbol}1 or more.`);
      return;
    }

    const amountCents = Math.round(amount * 100);
    const quantity = this.#unitPrice > 0 ? Math.round(amountCents / this.#unitPrice) : 1;
    if (quantity < 1) {
      this.#showError(`Please enter an amount of ${this.#symbol}1 or more.`);
      return;
    }
    const chargedCents = this.#unitPrice > 0 ? quantity * this.#unitPrice : amountCents;

    this.#isSubmitting = true;
    this.#setDisabled(true);
    this.#clearError();

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: [
        {
          id: this.#variantId,
          quantity,
          properties: {
            _Donation: formatMoney(chargedCents, this.#moneyFormat, this.#currency),
          },
        },
      ],
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data && data.status) {
        this.#showError(data.description || data.message || 'Something went wrong.');
        window.publishStorefrontEvent?.('cart_update_failed', {
          component: 'cart_donation',
          operation: 'donation_add',
          error_type: 'server_rejected',
          response_status: data.status || null,
        });
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'cart-donation', {
          source: 'cart-donation',
          itemCount: quantity,
          variantId: this.#variantId,
          sections: data?.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showError('Something went wrong. Please try again.');
      window.publishStorefrontEvent?.('cart_update_failed', {
        component: 'cart_donation',
        operation: 'donation_add',
        error_type: 'network',
      });
    } finally {
      this.#isSubmitting = false;
      this.#setDisabled(false);
    }
  }

  #setDisabled(disabled) {
    for (const button of this.querySelectorAll('button')) {
      if (disabled) button.setAttribute('disabled', '');
      else button.removeAttribute('disabled');
    }
  }

  #showError(message) {
    if (!this.#error) return;
    this.#error.textContent = message;
    this.#error.classList.remove('hidden');
  }

  #clearError = () => {
    if (!this.#error) return;
    this.#error.textContent = '';
    this.#error.classList.add('hidden');
  };
}

if (!customElements.get('cart-donation')) {
  customElements.define('cart-donation', CartDonation);
}
